import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Search, Filter, Package, Plus, Minus, MapPin, Loader2, X } from 'lucide-react';
import { 
  PRODUCT_CATEGORIES, 
  SKIN_CONCERNS, 
  getCategoryLabel, 
  getConcernLabel 
} from './constants';

const ProductCard = ({ product, inCart, cartQty, addToCart, updateQuantity, language }) => (
  <Card 
    className={`border overflow-hidden transition-all duration-300 ${
      inCart ? 'border-[#0EA5E9] bg-[#E0F2FE]/20' : 
      product.is_available === false ? 'border-[#E7E5E4] opacity-70' : 
      'border-[#E7E5E4] hover:shadow-lg'
    }`}
    data-testid={`product-card-${product.id}`}
  >
    <CardContent className="p-4">
      <div className="flex gap-4">
        <div className="w-20 h-20 bg-[#F5F5F4] rounded-lg flex-shrink-0 overflow-hidden">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-8 h-8 text-[#E7E5E4]" />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className={`font-medium text-sm line-clamp-2 ${product.is_available === false ? 'text-[#78716C]' : 'text-[#0F172A]'}`}>
                {product.name}
              </h3>
              <p className="text-xs text-[#0EA5E9] font-medium mt-1">{product.main_ingredient}</p>
            </div>
            <div className="flex flex-col gap-1 items-end">
              {product.is_available === false && (
                <Badge className="bg-red-100 text-red-700 text-[10px] flex-shrink-0">
                  {language === 'fr' ? 'Rupture' : 'Out of stock'}
                </Badge>
              )}
            </div>
          </div>
          
          {product.emplacement && (
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3 text-[#78716C]" />
              <span className="text-xs text-[#78716C]">{product.emplacement}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-3">
            <span className="font-bold text-[#0F172A]">
              {product.price?.toLocaleString('fr-FR') || '0'} FCFA
            </span>
            
            {inCart ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateQuantity(product.id, -1)}
                  className="w-8 h-8 rounded-full bg-[#F5F5F4] flex items-center justify-center hover:bg-[#E7E5E4]"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium">{cartQty}</span>
                <button
                  onClick={() => updateQuantity(product.id, 1)}
                  className="w-8 h-8 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center hover:bg-[#0284C7]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button
                onClick={() => addToCart(product)}
                size="sm"
                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-1" />
                {language === 'fr' ? 'Ajouter' : 'Add'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const CosmeticsTab = ({
  products,
  allRecommendations,
  loading,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  selectedConcern,
  setSelectedConcern,
  cart,
  addToCart,
  updateQuantity,
  diagnosis,
  language
}) => {
  const isInCart = (productId) => cart.some(item => item.product_id === productId);
  const getCartQuantity = (productId) => cart.find(item => item.product_id === productId)?.quantity || 0;

  return (
    <div className="space-y-4">
      {/* Recommended Products Section */}
      {allRecommendations.cosmetics?.length > 0 && (
        <Card className="bg-gradient-to-r from-[#C9A086]/10 to-[#E8DDD4]/30 border-[#C9A086]/30">
          <CardContent className="p-4">
            <h3 className="font-semibold text-[#5D4E37] mb-3 flex items-center gap-2">
              <span className="text-lg">⭐</span>
              {language === 'fr' ? 'Cosmétiques recommandés pour ce diagnostic' : 'Recommended cosmetics for this diagnosis'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {allRecommendations.cosmetics.slice(0, 4).map(product => (
                <Card key={product.id} className="bg-white border-[#E8DDD4] hover:shadow-md transition-shadow">
                  <CardContent className="p-3">
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-2 bg-[#FDF8F3] rounded-full flex items-center justify-center">
                        <Package className="w-6 h-6 text-[#C9A086]" />
                      </div>
                      <p className="text-xs font-medium text-[#5D4E37] line-clamp-2 mb-1">{product.name}</p>
                      <p className="text-sm font-bold text-[#C45C26]">{product.price?.toLocaleString()} F</p>
                      <Button 
                        size="sm" 
                        className="mt-2 w-full bg-[#C9A086] hover:bg-[#A67B5B] text-white text-xs h-7"
                        onClick={() => addToCart(product)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
          <Input
            placeholder={language === 'fr' ? 'Rechercher un produit...' : 'Search products...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 input-default"
            data-testid="product-search"
          />
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full whitespace-nowrap">
                <Filter className="w-4 h-4 mr-2" />
                {getCategoryLabel(selectedCategory, language)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-[300px] overflow-y-auto">
              {PRODUCT_CATEGORIES.map(cat => (
                <DropdownMenuItem 
                  key={cat.value} 
                  onClick={() => setSelectedCategory(cat.value)}
                  className={selectedCategory === cat.value ? 'bg-[#0EA5E9]/10 text-[#0EA5E9]' : ''}
                >
                  {language === 'fr' ? cat.label_fr : cat.label_en}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full whitespace-nowrap">
                <Package className="w-4 h-4 mr-2" />
                {getConcernLabel(selectedConcern, language)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-[300px] overflow-y-auto">
              {SKIN_CONCERNS.map(concern => (
                <DropdownMenuItem 
                  key={concern.value} 
                  onClick={() => setSelectedConcern(concern.value)}
                  className={selectedConcern === concern.value ? 'bg-[#0EA5E9]/10 text-[#0EA5E9]' : ''}
                >
                  {language === 'fr' ? concern.label_fr : concern.label_en}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {(selectedCategory !== 'all' || selectedConcern !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSelectedCategory('all'); setSelectedConcern('all'); }}
              className="text-[#78716C] hover:text-red-500"
            >
              <X className="w-4 h-4 mr-1" />
              {language === 'fr' ? 'Effacer' : 'Clear'}
            </Button>
          )}
        </div>

        {selectedConcern !== 'all' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              💡 {language === 'fr' 
                ? `Produits filtrés pour : ${getConcernLabel(selectedConcern, language)}. Consultez aussi les onglets Compléments et Consommables pour des recommandations adaptées.`
                : `Products filtered for: ${getConcernLabel(selectedConcern, language)}. Also check Supplements and Consumables tabs for tailored recommendations.`
              }
            </p>
          </div>
        )}
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <p className="text-[#78716C]">
            {language === 'fr' ? 'Aucun produit trouvé' : 'No products found'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              inCart={isInCart(product.id)}
              cartQty={getCartQuantity(product.id)}
              addToCart={addToCart}
              updateQuantity={updateQuantity}
              language={language}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CosmeticsTab;
